import {
    Field,
    FieldError,
    FieldLabel,
    Input as DSInput,
    Textarea as DSTextarea,
    Button,
} from "frontend-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";

import { CharacterCount } from "@dashboard/components/CharacterCount";
import type { AnnouncementIcon } from "@dashboard/api/task";

import { useFieldContext } from "./Form";
import {
    announcementIconKeys,
    announcementIconMap,
} from "@dashboard/features/Dashboard/Announcements/announcementIconMap";

interface InputWithCharCountProps
    extends React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
    label?: string;
    maxLength?: number;
    showCharCount?: boolean;
    charCountWarningThreshold?: number;
}

export function Input(props: InputWithCharCountProps) {
    const field = useFieldContext<string>();
    const {
        label,
        className,
        maxLength,
        showCharCount = false,
        charCountWarningThreshold = 0.8,
        ...inputProps
    } = props;

    const isInvalid = field.state.meta.errors.length > 0;
    const currentLength = String(field.state.value ?? "").length;

    return (
        <Field data-invalid={isInvalid}>
            {label && (
                <FieldLabel htmlFor={field.name} className="text-sm font-medium">
                    {label}
                </FieldLabel>
            )}
            <DSInput
                id={field.name}
                name={field.name}
                className={className}
                maxLength={maxLength}
                {...inputProps}
                value={String(field.state.value ?? "")}
                onChange={(ev) => field.handleChange(ev.target.value)}
                onBlur={field.handleBlur}
                aria-invalid={isInvalid}
            />
            {showCharCount && maxLength !== undefined && (
                <div className="flex justify-end">
                    <CharacterCount
                        currentLength={currentLength}
                        maxLength={maxLength}
                        warningThreshold={charCountWarningThreshold}
                    />
                </div>
            )}
            {isInvalid && <FieldError errors={field.state.meta.errors} />}
        </Field>
    );
}

interface TextAreaWithCharCountProps
    extends React.DetailedHTMLProps<
        React.TextareaHTMLAttributes<HTMLTextAreaElement>,
        HTMLTextAreaElement
    > {
    label?: string;
    maxLength?: number;
    showCharCount?: boolean;
    charCountWarningThreshold?: number;
}

export function TextArea(props: TextAreaWithCharCountProps) {
    const field = useFieldContext<string>();
    const {
        label,
        className,
        maxLength,
        showCharCount = false,
        charCountWarningThreshold = 0.8,
        ...textareaProps
    } = props;

    const isInvalid = field.state.meta.errors.length > 0;
    const currentLength = String(field.state.value ?? "").length;

    return (
        <Field data-invalid={isInvalid}>
            {label && (
                <FieldLabel htmlFor={field.name} className="text-sm font-medium">
                    {label}
                </FieldLabel>
            )}
            <DSTextarea
                id={field.name}
                name={field.name}
                className={className}
                maxLength={maxLength}
                {...textareaProps}
                value={String(field.state.value ?? "")}
                onChange={(ev) => field.handleChange(ev.target.value)}
                onBlur={field.handleBlur}
                aria-invalid={isInvalid}
            />
            {showCharCount && maxLength !== undefined && (
                <div className="flex justify-end">
                    <CharacterCount
                        currentLength={currentLength}
                        maxLength={maxLength}
                        warningThreshold={charCountWarningThreshold}
                    />
                </div>
            )}
            {isInvalid && <FieldError errors={field.state.meta.errors} />}
        </Field>
    );
}

export function IconSelect(props: { label?: string }) {
    const field = useFieldContext<AnnouncementIcon>();
    const { t } = useTranslation();

    return (
        <Field>
            {props.label && <FieldLabel className="text-sm font-medium">{props.label}</FieldLabel>}
            <div className="flex flex-wrap gap-2">
                {announcementIconKeys.map((key) => {
                    const entry = announcementIconMap[key];
                    const selected = field.state.value === key;
                    return (
                        <Button
                            key={key}
                            type="button"
                            onClick={() => field.handleChange(key)}
                            aria-pressed={selected}
                            className={`gap-2 rounded-submit px-3 py-2 text-[11px] text-compendia-gray-80 ${
                                selected
                                    ? "border-compendia-foreground bg-compendia-greige-40"
                                    : "border-compendia-border bg-compendia-card hover:bg-compendia-greige-10"
                            }`}
                        >
                            <FontAwesomeIcon icon={entry.icon} className="size-3.5" />
                            <span>{t(entry.labelKey)}</span>
                        </Button>
                    );
                })}
            </div>
        </Field>
    );
}
